/**
 * session — Auth Phase 1 (P1-B). The ONLY module that holds or touches an
 * auth token. Everything else asks getAccessToken().
 *
 *   v2 (when /health reports features.auth_v2): the 1-hour access token lives
 *   in module memory; the 30-day refresh token is an httpOnly cookie the
 *   browser sends only to /api/auth/v2/*. Nothing auth-related is written to
 *   localStorage except the non-secret device id.
 *
 *   v1 fallback (flag false / backend rolled back): the legacy localStorage
 *   'token' key, read and written HERE and nowhere else. The standing grep
 *   gate (frontend/scripts/check-token-access.sh) enforces that.
 *
 * Behaviour:
 *   - getAccessToken(): returns the cached token, or refreshes single-flight
 *     when it is missing or within REFRESH_MIN_MS of exp. Concurrent callers
 *     await the same refresh.
 *   - a proactive refresh fires REFRESH_AHEAD_MS before exp while the tab is
 *     visible; returning to a hidden tab refreshes if under REFRESH_AHEAD_MS.
 *   - a 401 from /refresh ends the session: memory cleared, 'lost' emitted.
 *     A network failure does NOT (the token stays; callers retry later).
 *   - bootstrap(): on load, exchange a legacy v1 token for a v2 session
 *     (deleting the key either way), else try a quiet refresh from the cookie.
 *   - sockets report 4401 through notifySessionLost(); a later successful
 *     login emits 'restored' so they reconnect.
 */

import { API_ORIGIN } from '../config/env';

const V2 = `${API_ORIGIN}/api/auth/v2`;
const LEGACY_KEY = 'token';          // v1 localStorage key — read/removed ONLY here
const DEVICE_KEY = 'gvc_device_id';  // non-secret, allowed in localStorage
const REFRESH_AHEAD_MS = 5 * 60 * 1000;
const REFRESH_MIN_MS = 60 * 1000;
const AUTH_FAILURE_MESSAGES = ['No token provided', 'Invalid token', 'Token expired', 'Session revoked'];

let mode = null;          // 'v2' | 'v1' | null (not bootstrapped)
let flag = null;          // /health features.auth_v2
let accessToken = null;
let exp = 0;              // ms epoch
let sid = null;           // session family id (v2)
let inflight = null;      // single-flight refresh promise
let timer = null;
let resetRequired = false; // P2-A (R6): the server says this password must be replaced first
const listeners = { lost: new Set(), restored: new Set(), resetRequired: new Set() };

// ---------- helpers ----------

const safeStorage = {
  get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* ignore */ } },
  remove: (k) => { try { localStorage.removeItem(k); } catch (e) { /* ignore */ } }
};

const randomId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
};

export const getDeviceId = () => {
  let id = safeStorage.get(DEVICE_KEY);
  if (!id) { id = randomId(); safeStorage.set(DEVICE_KEY, id); }
  return id;
};

const decodePayload = (token) => {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch (e) { return {}; }
};

const emit = (event, detail) => { listeners[event].forEach((cb) => { try { cb(detail); } catch (e) { /* listener error */ } }); };
const on = (event, cb) => { listeners[event].add(cb); return () => listeners[event].delete(cb); };
export const onSessionLost = (cb) => on('lost', cb);
export const onSessionRestored = (cb) => on('restored', cb);
export const onPasswordResetRequired = (cb) => on('resetRequired', cb);

/** True when a 401 body means "your token is bad", not e.g. "wrong PIN". */
export const isAuthFailure = (body) => {
  if (!body || typeof body !== 'object') return true; // no body → treat as auth failure
  if (body.code === 'SESSION_REVOKED') return true;
  return AUTH_FAILURE_MESSAGES.includes(body.message);
};

const setToken = (token) => {
  accessToken = token;
  const p = decodePayload(token);
  exp = (p.exp || 0) * 1000;
  sid = p.sid || null;
  schedule();
};

export const clearSession = () => {
  accessToken = null; exp = 0; sid = null; resetRequired = false;
  if (timer) { clearTimeout(timer); timer = null; }
};

// ---------- forced password reset (Auth Phase 2, P2-A, ruling R6) ----------
// The flag is set from the v2 login/exchange/refresh body
// (password_reset_required: true) or by any 403 PASSWORD_RESET_REQUIRED seen
// by the API clients. It clears when a later issued token comes without the
// flag — i.e. the refresh that follows a successful change-password.
export const isPasswordResetRequired = () => resetRequired;
const setResetRequired = (required, via) => {
  if (required === resetRequired) return;
  resetRequired = required;
  emit('resetRequired', { required, via });
};
/** API clients call this on a 403 whose body code is PASSWORD_RESET_REQUIRED. */
export const notifyPasswordResetRequired = () => setResetRequired(true, '403');
/** Apply a login-shaped body (token + optional password_reset_required). */
const applyIssued = (data, via) => {
  setToken(data.token);
  setResetRequired(!!data.password_reset_required, via);
};

const schedule = () => {
  if (timer) { clearTimeout(timer); timer = null; }
  if (mode !== 'v2' || !exp) return;
  const delay = Math.max(exp - Date.now() - REFRESH_AHEAD_MS, 5000);
  timer = setTimeout(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return; // visibility return handles it
    refresh().catch(() => { /* lost/network — handled inside */ });
  }, delay);
};

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && mode === 'v2' && accessToken && exp - Date.now() < REFRESH_AHEAD_MS) {
      refresh().catch(() => { /* handled inside */ });
    }
  });
}

// ---------- cross-tab sync (rehearsal finding, 2026-09-04) ----------
// Tabs share one cookie jar but each has its own in-memory session. After a
// revoke every tab shows the modal; without sync each sign-in would mint a
// separate family and strand the earlier one. So: a tab that signs in tells
// the others to resume from the (new) cookie; a tab that signs out tells the
// others their session is gone.
let channel = null;
try {
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel('gvc-session');
    channel.onmessage = (ev) => {
      const msg = ev.data || {};
      if (msg.type === 'login' && mode === 'v2') {
        refresh({ quiet: true }).then(() => emit('restored', { sid, via: 'other-tab' })).catch(() => { /* not our session */ });
      } else if (msg.type === 'logout') {
        clearSession();
        emit('lost', { code: 'SIGNED_OUT_ELSEWHERE', message: 'Signed out in another tab' });
      }
    };
  }
} catch (e) { channel = null; }
const broadcast = (type) => { try { if (channel) channel.postMessage({ type }); } catch (e) { /* ignore */ } };

const v2Post = (path, { body, bearer } = {}) => fetch(`${V2}${path}`, {
  method: 'POST',
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
    'X-Device-Id': getDeviceId(),
    ...(bearer && { Authorization: `Bearer ${bearer}` })
  },
  body: body ? JSON.stringify(body) : undefined
});

// ---------- public API ----------

export const getMode = () => mode;
export const isV2 = () => mode === 'v2';
export const getSessionId = () => sid;
export const hasToken = () => (mode === 'v1' ? !!safeStorage.get(LEGACY_KEY) : !!accessToken);

/** Single-flight rotation. Resolves the login-shaped payload; rejects on failure. */
export const refresh = ({ quiet = false } = {}) => {
  if (inflight) return inflight;
  inflight = (async () => {
    let res;
    try { res = await v2Post('/refresh'); } catch (e) {
      const err = new Error('network'); err.network = true; throw err; // token kept; caller retries later
    }
    if (res.status === 401) {
      const body = await res.json().catch(() => ({}));
      clearSession();
      if (!quiet) emit('lost', { code: body.code || 'REFRESH_FAILED', message: body.message });
      const err = new Error(body.message || 'Session expired'); err.code = body.code; err.status = 401; throw err;
    }
    if (!res.ok) { const err = new Error(`refresh ${res.status}`); err.status = res.status; throw err; }
    const data = await res.json();
    applyIssued(data, 'refresh');
    return data;
  })().finally(() => { inflight = null; });
  return inflight;
};

export const getAccessToken = async () => {
  if (mode === 'v1') return safeStorage.get(LEGACY_KEY);
  if (mode !== 'v2') return accessToken;
  if (accessToken && Date.now() < exp - REFRESH_MIN_MS) return accessToken;
  try { await refresh(); } catch (e) { /* lost or network */ }
  return accessToken;
};

/** Decide v1/v2 from /health, exchange a legacy token, or resume from the cookie. Returns "signed in?". */
export const bootstrap = async () => {
  if (flag === null) {
    try {
      const r = await fetch(`${API_ORIGIN}/health`);
      const h = await r.json();
      flag = !!(h.features && h.features.auth_v2);
    } catch (e) { flag = false; }
  }
  if (!flag) { mode = 'v1'; return !!safeStorage.get(LEGACY_KEY); }
  mode = 'v2';

  const legacy = safeStorage.get(LEGACY_KEY);
  if (legacy) {
    try {
      const r = await v2Post('/exchange', { bearer: legacy });
      safeStorage.remove(LEGACY_KEY);
      if (r.ok) { const data = await r.json(); applyIssued(data, 'exchange'); return true; }
    } catch (e) { safeStorage.remove(LEGACY_KEY); }
  }
  try { await refresh({ quiet: true }); return true; } catch (e) { return false; }
};

export const login = async (identifier, password) => {
  if (mode === 'v1') {
    const r = await fetch(`${API_ORIGIN}/api/auth/login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ identifier, password })
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message || 'Login failed');
    safeStorage.set(LEGACY_KEY, data.token);
    return data.user;
  }
  const r = await v2Post('/login', { body: { identifier, password } });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || 'Login failed');
  applyIssued(data, 'login');
  emit('restored', { sid });
  broadcast('login');
  return data.user;
};

export const logout = async () => {
  if (mode === 'v2') { try { await v2Post('/logout'); } catch (e) { /* best effort */ } }
  safeStorage.remove(LEGACY_KEY);
  clearSession();
  broadcast('logout');
  try { sessionStorage.removeItem('vault_verified'); sessionStorage.removeItem('fleet_unlocked'); } catch (e) { /* ignore */ }
};

/** Sockets call this on close code 4401. */
export const notifySessionLost = (detail = { code: 'SESSION_REVOKED' }) => { clearSession(); emit('lost', detail); };

// rehearsal/test hook — never used by app code
export const _reset = () => { mode = null; flag = null; clearSession(); inflight = null; listeners.lost.clear(); listeners.restored.clear(); listeners.resetRequired.clear(); };
